/**
 * Pure statutory-deadline logic for the Government Portal D&U workflow.
 *
 * The escalation step screens each render a single statutory clock in isolation;
 * nothing pulls them together, so an officer carrying a caseload has no one place
 * to see what is coming due. This module derives *every* active statutory clock
 * from the dates the officer already recorded (notice service date, committee
 * decision date, warrant follow-up) — never a separate timer to wind.
 *
 * It is the single source of truth shared by gov-api's aggregate `/deadlines`
 * endpoint, the messaging-worker reminder scheduler, and the dashboard. Inputs
 * are described structurally so the lib carries no contracts/entity dependency.
 */

// --- clocks ----------------------------------------------------------------

export const APPEAL_WINDOW_DAYS = 14;
export const KINGS_BENCH_WINDOW_DAYS = 14;
// Provincial Court proceedings limitation — an information must be laid within
// six months of the offence (POPA s. 21(1)).
export const PROCEEDINGS_LIMITATION_MONTHS = 6;

export interface StatutoryClock {
  deadlineIso: string; // yyyy-mm-dd — last day of the window
  daysRemaining: number; // negative once the window has closed
  expired: boolean;
}

/** Backwards-compatible alias — the appeal window is one statutory clock. */
export type AppealClock = StatutoryClock;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The calendar every statutory clock is read in. "Today" is the day in New
 * Brunswick, not wherever the API container's clock happens to be set (UTC in
 * production): from 20:00 or 21:00 Atlantic time onward the UTC date is
 * already tomorrow, which used to mark a window closed hours before it was.
 * The same value as `ENFORCEMENT_TIME_ZONE` in `domain-citizen-portal`; this
 * lib carries no dependencies, so it is restated rather than imported.
 */
export const DEADLINE_TIME_ZONE = 'America/Moncton';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `yyyy-mm-dd` of an instant in the enforcement calendar. */
function calendarDateAt(instant: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: DEADLINE_TIME_ZONE,
  }).format(instant);
}

/**
 * A `yyyy-mm-dd` as a day count, by pure calendar arithmetic (UTC, so no DST
 * hour can make a day 23 or 25 hours long). Null for anything that is not a
 * real calendar date — `2026-02-30` included.
 */
function dayNumber(iso: string): number | null {
  const m = DATE_ONLY.exec(iso);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = Date.UTC(y, mo - 1, d);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return null;
  }
  return t / MS_PER_DAY;
}

function fromDayNumber(n: number): string {
  return new Date(n * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * The calendar day a stored value names. A bare `yyyy-mm-dd` is already one; a
 * timestamp is read as the day it fell on in the enforcement calendar — not its
 * UTC date, which for an evening timestamp is the next day.
 */
function calendarDateOf(value: string): string | null {
  if (DATE_ONLY.test(value)) return dayNumber(value) === null ? null : value;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime()) ? null : calendarDateAt(instant);
}

function clockFor(deadlineDay: number, now: Date): StatutoryClock {
  const today = dayNumber(calendarDateAt(now)) as number;
  const daysRemaining = deadlineDay - today;
  return { deadlineIso: fromDayNumber(deadlineDay), daysRemaining, expired: daysRemaining < 0 };
}

/**
 * A statutory countdown window of `days` days running from `startDateIso`.
 * Returns null until a start date is recorded. Day-of (daysRemaining === 0)
 * still counts as open; the window is expired only once it has passed.
 * "Today" is the calendar day in {@link DEADLINE_TIME_ZONE}.
 */
export function statutoryClock(
  startDateIso: string,
  days: number,
  now: Date = new Date(),
): StatutoryClock | null {
  if (!startDateIso) return null;
  const start = dayNumber(startDateIso);
  if (start === null) return null;
  return clockFor(start + days, now);
}

/**
 * The 14-day appeal window (LGA s. 134(1)) runs from the date the notice was
 * served. s. 134(2) is a different rule — the notice becomes final and binding
 * when no appeal is filed — so it is not the citation for this clock.
 */
export function appealClock(serviceDateIso: string, now: Date = new Date()): AppealClock | null {
  return statutoryClock(serviceDateIso, APPEAL_WINDOW_DAYS, now);
}

/**
 * A statutory countdown expressed in calendar months from `startDateIso` — used
 * for the six-month proceedings limitation, which the day-based window would
 * only approximate. The target day is clamped to the last day of the target
 * month rather than rolling over (Aug 31 + 6 → Feb 28, never Mar 3): for a
 * limitation clock a later, overly-generous deadline is the unsafe direction.
 * Returns null until a start date is set.
 */
export function monthsClock(
  startDateIso: string,
  months: number,
  now: Date = new Date(),
): StatutoryClock | null {
  if (!startDateIso) return null;
  if (dayNumber(startDateIso) === null) return null;
  const [y, m, d] = startDateIso.split('-').map(Number);

  // Add months on the first of the month, then clamp the day to that month's
  // length so overflow days never spill into (and extend past) the next month.
  const firstOfTarget = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDayOfMonth = new Date(
    Date.UTC(firstOfTarget.getUTCFullYear(), firstOfTarget.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const deadline = Date.UTC(
    firstOfTarget.getUTCFullYear(),
    firstOfTarget.getUTCMonth(),
    Math.min(d, lastDayOfMonth),
  );
  return clockFor(deadline / MS_PER_DAY, now);
}

/**
 * The six-month Provincial Court proceedings limitation (POPA s. 21(1)) runs
 * from the officer-entered offence date.
 */
export function proceedingsLimitationClock(
  offenseDateIso: string,
  now: Date = new Date(),
): StatutoryClock | null {
  return monthsClock(offenseDateIso, PROCEEDINGS_LIMITATION_MONTHS, now);
}

/**
 * The compressed compliance window that applies when the s. 144(9) emergency
 * bypass is used.
 *
 * **The length is not a statutory constant and none is asserted here.** s. 144(9)
 * removes the requirement to give sufficient notice and to attend at a
 * reasonable hour — both about inspection; it says nothing about how long the
 * owner then has to comply. The v47 flowchart is explicit that the compliance
 * deadline "may be shortened" and "may be adjusted", i.e. it is the officer's
 * decision on the facts. So `days` is passed in from the officer's recorded
 * declaration rather than read from a constant in this module.
 *
 * The window itself behaves exactly like every other clock here — day-of still
 * counts as open — so a compressed deadline cannot be off by one against the
 * ordinary ones an officer reads beside it.
 */
export function emergencyComplianceClock(
  declaredDateIso: string,
  days: number,
  now: Date = new Date(),
): StatutoryClock | null {
  // A zero or negative window is not a compression, it is an expired deadline
  // at the moment of declaration. Refused rather than derived: the officer must
  // record a real period.
  if (!Number.isInteger(days) || days < 1) return null;
  return statutoryClock(declaredDateIso, days, now);
}

/**
 * A statutory clock expressed as an absolute due date rather than a start +
 * window — e.g. the entry-warrant follow-up tickler, whose due date is computed
 * elsewhere. Accepts an ISO date or datetime; a datetime is reduced to the day
 * it falls on in {@link DEADLINE_TIME_ZONE}, so day-of (daysRemaining === 0)
 * still counts as open, matching {@link statutoryClock}.
 */
export function clockFromDeadline(
  deadlineIso: string,
  now: Date = new Date(),
): StatutoryClock | null {
  if (!deadlineIso) return null;
  const due = calendarDateOf(deadlineIso);
  if (due === null) return null;
  return clockFor(dayNumber(due) as number, now);
}

// --- deadline derivation ---------------------------------------------------

export type DeadlineKind =
  | 'appeal'
  | 'kings_bench'
  | 'warrant_followup'
  | 'six_month'
  | 'emergency_compliance'
  | 'appeal_compliance'
  // A clock from a configured by-law workflow (Property Standards, Noise, …).
  // Which one is in `ruleKey`, and what to call it in `label`.
  | 'bylaw';

/**
 * Each clock's name in both languages, for text written outside a browser —
 * email, the inbox, a calendar feed. Exhaustive over {@link DeadlineKind}, so a
 * new clock is a compile error here rather than an untitled calendar entry.
 */
export const DEADLINE_KIND_LABELS: Record<DeadlineKind, { en: string; fr: string }> = {
  appeal: { en: 'Appeal window', fr: 'Délai d’appel' },
  kings_bench: { en: 'King’s Bench window', fr: 'Délai de la Cour du Banc du Roi' },
  warrant_followup: { en: 'Warrant follow-up', fr: 'Suivi du mandat' },
  six_month: {
    en: 'Proceedings limitation (6 mo.)',
    fr: 'Prescription des poursuites (6 mois)',
  },
  emergency_compliance: {
    en: 'Emergency compliance (s. 144(9))',
    fr: 'Conformité d’urgence (art. 144(9))',
  },
  appeal_compliance: { en: 'Compliance date set on appeal', fr: 'Échéance fixée en appel' },
  bylaw: { en: 'By-law deadline', fr: 'Délai prévu par l’arrêté' },
};

/** One statutory clock, enriched with the file context a dashboard needs. */
export interface DeadlineItem {
  /** Stable per-row id — `${fileId}:${kind}`, or the warrant id for follow-ups. */
  id: string;
  fileId: string;
  reference: string;
  address: string;
  kind: DeadlineKind;
  /** The statutory provision the clock hangs off, surfaced discreetly. */
  statuteRef: string;
  deadlineIso: string;
  daysRemaining: number;
  expired: boolean;
  /** For `bylaw` clocks: the rule's key in the file's workflow definition. */
  ruleKey?: string;
  /** For `bylaw` clocks: the rule's name in both languages. */
  label?: { en: string; fr: string };
}

/**
 * Structural view of a file the derivation reads — only the fields it needs.
 * `FileDetail` from contracts-gov is assignable to this, so callers can pass
 * their entities/DTOs directly without this lib depending on them.
 */
export interface DeadlineFileInput {
  id: string;
  reference: string;
  address: string;
  stageData?: {
    site_visit?: { offenseDate?: string | null } | null;
    step_5?: { serviceMethod?: string | null; serviceDate?: string | null } | null;
    step_6?: {
      appealFiled?: boolean | null;
      committeeDecision?: string | null;
      decisionDate?: string | null;
      revisedComplianceDeadline?: string | null;
    } | null;
    step_9?: { filed?: boolean | null } | null;
  } | null;
}

/**
 * Structural view of an s. 144(9) declaration — only what the clock needs. A
 * file can hold more than one (the bypass has two entry points, Step 5 and
 * Step 8, which the flowchart says may run simultaneously).
 */
export interface DeadlineEmergencyInput {
  id: string;
  /** ISO date the emergency was declared — the clock starts here. */
  declaredAt: string;
  /** Officer-set compressed window; null when no period was given to anyone. */
  complianceDays: number | null;
  /** A withdrawn declaration stops its clock without erasing the record. */
  revokedAt: string | null;
}

/** Structural view of an entry warrant — the follow-up tickler fields only. */
export interface DeadlineWarrantInput {
  id: string;
  status: string;
  applicableStatute: string;
  followUpDueAt: string | null;
}

/** Warrant statuses still in play — a closed/decided warrant has no live tickler. */
const ACTIVE_WARRANT_STATUSES = new Set<string>(['filed', 'granted', 'extension_pending']);

const STATUTE_REF: Record<Exclude<DeadlineKind, 'warrant_followup' | 'bylaw'>, string> = {
  appeal: 'LGA s. 134(1)',
  kings_bench: 'LGA s. 135',
  six_month: 'POPA s. 21(1)',
  // The bypass hangs off s. 144(9); the *length* of the window does not — see
  // `emergencyComplianceClock`.
  emergency_compliance: 'LGA s. 144(9)',
  // The date the Appeal Committee substituted when it varied the notice.
  appeal_compliance: 'LGA s. 134',
};

function toItem(
  file: DeadlineFileInput,
  kind: DeadlineKind,
  statuteRef: string,
  clock: StatutoryClock,
  id: string,
): DeadlineItem {
  return {
    id,
    fileId: file.id,
    reference: file.reference,
    address: file.address,
    kind,
    statuteRef,
    deadlineIso: clock.deadlineIso,
    daysRemaining: clock.daysRemaining,
    expired: clock.expired,
  };
}

/**
 * Every active statutory clock on a single file. Mirrors the gating the step
 * screens use so a dashboard never contradicts what the officer sees there:
 *
 * - **Appeal window** — runs from the Step 5 service date, but only while the
 *   appeal outcome is still open; once the committee has recorded a decision the
 *   window is moot.
 * - **King's Bench window** — runs from the Step 6 decision date, when an appeal
 *   was filed and the committee did not rescind.
 * - **Warrant follow-up** — the `followUpDueAt` tickler on any warrant still in
 *   play.
 */
export function deriveFileDeadlines(
  file: DeadlineFileInput,
  warrants: DeadlineWarrantInput[],
  now: Date = new Date(),
  emergencies: DeadlineEmergencyInput[] = [],
): DeadlineItem[] {
  const items: DeadlineItem[] = [];
  const bag = file.stageData ?? {};
  const siteVisit = bag.site_visit ?? {};
  const step5 = bag.step_5 ?? {};
  const step6 = bag.step_6 ?? {};
  const step9 = bag.step_9 ?? {};

  // Six-month proceedings limitation — live once an offence date is recorded,
  // until the information is actually laid (Step 9 filed).
  if (siteVisit.offenseDate && !step9.filed) {
    const clock = proceedingsLimitationClock(siteVisit.offenseDate, now);
    if (clock) {
      items.push(toItem(file, 'six_month', STATUTE_REF.six_month, clock, `${file.id}:six_month`));
    }
  }

  if (step5.serviceMethod && step5.serviceDate && !step6.committeeDecision) {
    const clock = appealClock(step5.serviceDate, now);
    if (clock) items.push(toItem(file, 'appeal', STATUTE_REF.appeal, clock, `${file.id}:appeal`));
  }

  if (
    step6.appealFiled &&
    step6.committeeDecision &&
    step6.committeeDecision !== 'rescind' &&
    step6.decisionDate
  ) {
    const clock = statutoryClock(step6.decisionDate, KINGS_BENCH_WINDOW_DAYS, now);
    if (clock) {
      items.push(
        toItem(file, 'kings_bench', STATUTE_REF.kings_bench, clock, `${file.id}:kings_bench`),
      );
    }
  }

  // A committee that varied the notice set a new date to comply by. It runs
  // from the decision, not from service, and only while the variation stands
  // (a rescind leaves nothing to comply with).
  if (
    step6.appealFiled &&
    step6.revisedComplianceDeadline &&
    (step6.committeeDecision === 'modify' || step6.committeeDecision === 'extend')
  ) {
    const clock = clockFromDeadline(step6.revisedComplianceDeadline, now);
    if (clock) {
      items.push(
        toItem(
          file,
          'appeal_compliance',
          STATUTE_REF.appeal_compliance,
          clock,
          `${file.id}:appeal_compliance`,
        ),
      );
    }
  }

  // s. 144(9) compressed compliance — one clock per live declaration. Revoked
  // declarations keep their row but stop their clock, and a declaration with no
  // period (the municipality is doing the work itself) has no clock to run.
  for (const emergency of emergencies) {
    if (emergency.revokedAt || !emergency.complianceDays || !emergency.declaredAt) continue;
    // A timestamp's calendar day in New Brunswick, not its UTC date: a
    // declaration made at 21:00 Atlantic is that day's, not tomorrow's.
    const declaredOn = calendarDateOf(emergency.declaredAt);
    if (!declaredOn) continue;
    const clock = emergencyComplianceClock(declaredOn, emergency.complianceDays, now);
    if (clock) {
      items.push(
        toItem(
          file,
          'emergency_compliance',
          STATUTE_REF.emergency_compliance,
          clock,
          `${file.id}:emergency:${emergency.id}`,
        ),
      );
    }
  }

  for (const warrant of warrants) {
    if (!ACTIVE_WARRANT_STATUSES.has(warrant.status) || !warrant.followUpDueAt) continue;
    const clock = clockFromDeadline(warrant.followUpDueAt, now);
    if (clock) {
      items.push(toItem(file, 'warrant_followup', warrant.applicableStatute, clock, warrant.id));
    }
  }

  return items;
}

/**
 * Aggregate and sort every file's deadlines. Closest due date first, so the most
 * overdue (most-negative `daysRemaining`) rises to the top.
 */
export function deriveDeadlines(
  files: DeadlineFileInput[],
  warrantsByFile: Record<string, DeadlineWarrantInput[]>,
  now: Date = new Date(),
  emergenciesByFile: Record<string, DeadlineEmergencyInput[]> = {},
): DeadlineItem[] {
  return files
    .flatMap((f) =>
      deriveFileDeadlines(f, warrantsByFile[f.id] ?? [], now, emergenciesByFile[f.id] ?? []),
    )
    .sort((a, b) => a.deadlineIso.localeCompare(b.deadlineIso));
}

// --- configured by-law clocks ------------------------------------------------

/**
 * One live clock of a configured by-law workflow, already chosen as live by the
 * workflow engine (`liveDeadlineStarts` in `@munifocus/domain-stages`): this
 * module only does the arithmetic, in the same calendar as every other clock.
 */
export interface BylawDeadlineInput {
  key: string;
  label: { en: string; fr: string };
  statuteRef: string;
  startIso: string;
  unit: 'days' | 'months';
  length: number;
}

/** A clock of `length` days or calendar months from `startIso`. */
export function ruleClock(
  startIso: string,
  unit: 'days' | 'months',
  length: number,
  now: Date = new Date(),
): StatutoryClock | null {
  return unit === 'months'
    ? monthsClock(startIso, length, now)
    : statutoryClock(startIso, length, now);
}

/** The configured clocks on one file, as dashboard rows. */
export function deriveBylawDeadlines(
  file: Pick<DeadlineFileInput, 'id' | 'reference' | 'address'>,
  live: BylawDeadlineInput[],
  now: Date = new Date(),
): DeadlineItem[] {
  const items: DeadlineItem[] = [];
  for (const rule of live) {
    const clock = ruleClock(rule.startIso, rule.unit, rule.length, now);
    if (!clock) continue;
    items.push({
      id: `${file.id}:bylaw:${rule.key}`,
      fileId: file.id,
      reference: file.reference,
      address: file.address,
      kind: 'bylaw',
      statuteRef: rule.statuteRef,
      deadlineIso: clock.deadlineIso,
      daysRemaining: clock.daysRemaining,
      expired: clock.expired,
      ruleKey: rule.key,
      label: rule.label,
    });
  }
  return items;
}
